import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Check, X } from "lucide-react";

export default function Admin() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/lesson-videos").then((r) => setVideos(r.data.lessons)).catch(() => {});
  }, []);

  const reloadVideos = () => api.get("/admin/lesson-videos").then((r) => setVideos(r.data.lessons));

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-[#A36B5E]">Admin</div>
      <h1 className="font-display text-5xl text-[#2D2A26] mt-3 tracking-tight">Operations</h1>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
          <Stat label="Total Users" value={stats.total_users} />
          <Stat label="Paid Users" value={stats.paid_users} />
          <Stat label="Speaker Requests" value={stats.total_bookings} />
          <Stat label="Assessments Taken" value={stats.total_assessments} />
        </div>
      )}

      <div className="mt-10 border-b border-[#EAE5D9] flex gap-6 flex-wrap">
        <Tab active={tab === "bookings"} onClick={() => setTab("bookings")} testid="admin-tab-bookings">Speaker Bookings</Tab>
        <Tab active={tab === "users"} onClick={() => setTab("users")} testid="admin-tab-users">Students</Tab>
        <Tab active={tab === "videos"} onClick={() => setTab("videos")} testid="admin-tab-videos">Lesson Videos</Tab>
      </div>

      {tab === "bookings" && (
        <div className="mt-8 space-y-4">
          {bookings.length === 0 && <div className="text-[#8A857D] italic">No bookings yet.</div>}
          {bookings.map((b) => (
            <div key={b.id} className="bg-white border border-[#EAE5D9] rounded-2xl p-6" data-testid={`booking-${b.id}`}>
              <div className="flex flex-wrap justify-between items-baseline gap-2">
                <div className="font-display text-xl text-[#2D2A26]">{b.name}</div>
                <div className="text-xs text-[#8A857D]">{b.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="text-sm text-[#5C5852] mt-1">{b.email} · {b.platform} {b.handle && `· @${b.handle}`}</div>
              <div className="mt-3 text-[#2D2A26]"><strong>Topic:</strong> {b.topic}</div>
              <div className="mt-2 text-[#5C5852]">{b.message}</div>
              {b.audience_size && <div className="mt-2 text-xs text-[#8A857D]">Audience: {b.audience_size}</div>}
              {b.preferred_date && <div className="text-xs text-[#8A857D]">Preferred: {b.preferred_date}</div>}
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="mt-8 bg-white border border-[#EAE5D9] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB]">
              <tr className="text-left text-xs uppercase tracking-widest text-[#5C5852]">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Stage</th><th className="px-4 py-3">Paid</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[#EAE5D9]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-[#2D2A26]">{u.name}</td>
                  <td className="px-4 py-3 text-[#5C5852]">{u.email}</td>
                  <td className="px-4 py-3 text-[#5C5852]">{u.alignment_stage || "—"}</td>
                  <td className="px-4 py-3 text-[#5C5852]">{u.has_paid ? "Yes" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "videos" && (
        <div className="mt-8">
          <p className="text-sm text-[#5C5852] mb-6 max-w-2xl">
            Paste a YouTube URL for each lesson. Leave blank to remove the video. Supported formats:
            <span className="text-[#2D2A26]"> youtu.be/...</span>,
            <span className="text-[#2D2A26]"> youtube.com/watch?v=...</span>,
            <span className="text-[#2D2A26]"> youtube.com/embed/...</span>
          </p>
          <div className="space-y-3">
            {videos.map((v) => (
              <VideoRow key={`${v.pillar_id}-${v.lesson_id}`} item={v} onSaved={reloadVideos} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VideoRow({ item, onSaved }) {
  const [url, setUrl] = useState(item.video_url || "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error

  const save = async () => {
    setStatus("saving");
    try {
      await api.post("/admin/lesson-videos", {
        pillar_id: item.pillar_id,
        lesson_id: item.lesson_id,
        video_url: url,
      });
      setStatus("saved");
      onSaved && onSaved();
      setTimeout(() => setStatus("idle"), 1800);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="bg-white border border-[#EAE5D9] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`video-row-${item.pillar_id}-${item.lesson_id}`}>
      <div className="sm:w-72">
        <div className="text-xs uppercase tracking-widest text-[#A36B5E]">{item.pillar_title}</div>
        <div className="text-[#2D2A26] mt-1">{item.lesson_title}</div>
      </div>
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://youtube.com/watch?v=..."
        data-testid={`video-url-input-${item.pillar_id}-${item.lesson_id}`}
        className="flex-1 px-4 py-2.5 rounded-xl border border-[#EAE5D9] bg-[#FDFBF7] text-sm focus:outline-none focus:border-[#A36B5E]"
      />
      <button
        onClick={save}
        disabled={status === "saving"}
        data-testid={`video-save-${item.pillar_id}-${item.lesson_id}`}
        className="btn-primary rounded-full px-5 py-2 text-sm font-medium whitespace-nowrap disabled:opacity-50"
      >
        {status === "saving" ? "Saving..." : status === "saved" ? "Saved ✓" : "Save"}
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-[#8A857D]">{label}</div>
      <div className="font-display text-3xl text-[#2D2A26] mt-1">{value}</div>
    </div>
  );
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`pb-3 -mb-px text-sm tracking-wide border-b-2 transition-colors ${
        active ? "text-[#A36B5E] border-[#A36B5E]" : "text-[#5C5852] border-transparent hover:text-[#2D2A26]"
      }`}
    >
      {children}
    </button>
  );
}
